import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gear, ArrowsClockwise, Play, CheckCircle, Clock, Eye } from '@phosphor-icons/react';
import { PracticeBadge } from './PracticeBadge';

const STATUS_CONFIG = {
  UPCOMING: { label: 'Upcoming', color: 'text-[#9E9E9E]', bg: 'bg-[#262626]', icon: Clock },
  LIVE: { label: 'Live', color: 'text-[#FF3B30]', bg: 'bg-[#FF3B30]/15', icon: Play },
  COMPLETED: { label: 'Completed', color: 'text-[#34C759]', bg: 'bg-[#34C759]/15', icon: CheckCircle },
};

export const ClassList = ({ classesByDay, selectedClass, isAdmin, onSelectClass, onConfigureDivisions, onSetClassStatus, onUpdateClassPayback }) => {
  const navigate = useNavigate();
  const [editingPaybackId, setEditingPaybackId] = useState(null);
  const dayKeys = Object.keys(classesByDay);
  const isMultiDay = dayKeys.length > 1 || (dayKeys.length === 1 && dayKeys[0] !== 'All Days');
  const [activeDayTab, setActiveDayTab] = useState(dayKeys[0] || '');

  const displayDays = isMultiDay ? [activeDayTab] : dayKeys;

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Classes & Events</h2>

      {isMultiDay && (
        <div className="flex gap-1 mb-4 bg-[#161616] rounded-lg p-1 overflow-x-auto" data-testid="show-day-tabs">
          {dayKeys.map((day) => {
            const count = classesByDay[day]?.length || 0;
            return (
              <button
                key={day}
                onClick={() => setActiveDayTab(day)}
                data-testid={`show-day-tab-${day}`}
                className={`relative flex-shrink-0 px-4 py-2.5 rounded-md text-sm font-bold transition-all ${
                  activeDayTab === day
                    ? 'bg-[#0096FF] text-white'
                    : 'text-[#9E9E9E] hover:text-white hover:bg-[#262626]'
                }`}
              >
                {day}
                <span className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black ${
                  activeDayTab === day ? 'bg-white/25 text-white' : 'bg-[#262626] text-[#9E9E9E]'
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {displayDays.map((day) => (
        <div key={day} className="mb-6">
          {!isMultiDay && dayKeys.length > 1 && (
            <h3 className="text-lg font-bold text-[#0096FF] mb-3" style={{fontFamily: 'Outfit'}}>
              {day}
            </h3>
          )}
          <div className="space-y-3">
            {(classesByDay[day] || []).map((cls) => {
              const status = cls.status || 'UPCOMING';
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.UPCOMING;
              const StatusIcon = cfg.icon;
              return (
                <div
                  key={cls.id}
                  data-testid={`class-card-${cls.id}`}
                  onClick={() => onSelectClass(cls)}
                  className={`bg-[#161616] border rounded-lg p-4 transition-all duration-200 cursor-pointer ${
                    selectedClass?.id === cls.id
                      ? 'border-[#0096FF] ring-1 ring-[#0096FF]/30'
                      : status === 'LIVE' ? 'border-[#FF3B30]/40' : 'border-[#262626] hover:border-[#0096FF]/50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-base font-bold text-white truncate" style={{fontFamily: 'Outfit'}}>{cls.name}</h4>
                        <PracticeBadge classType={cls.class_type} />
                        {status === 'LIVE' && (
                          <span className="relative flex h-2 w-2 flex-shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF3B30]"></span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[#9E9E9E]">
                        <span>${cls.entry_fee}</span>
                        {isAdmin && onUpdateClassPayback && editingPaybackId === cls.id ? (
                          <select
                            value={cls.payback_percent}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); onUpdateClassPayback(cls.id, parseFloat(e.target.value)); setEditingPaybackId(null); }}
                            onBlur={() => setEditingPaybackId(null)}
                            autoFocus
                            className="px-1 py-0.5 bg-[#0A0A0A] border border-[#0096FF] rounded text-white text-sm focus:outline-none"
                            data-testid={`class-payback-edit-${cls.id}`}
                          >
                            {[50, 60, 65, 70, 75, 80, 85, 90, 100].map(p => (
                              <option key={p} value={p}>{p}%</option>
                            ))}
                          </select>
                        ) : (
                          <span
                            onClick={(e) => { if (isAdmin && onUpdateClassPayback) { e.stopPropagation(); setEditingPaybackId(cls.id); }}}
                            className={isAdmin && onUpdateClassPayback ? 'cursor-pointer hover:text-[#0096FF] transition-colors' : ''}
                            data-testid={`class-payback-${cls.id}`}
                            title={isAdmin ? 'Click to edit payback' : ''}
                          >
                            {cls.payback_percent}% payback
                          </span>
                        )}
                        {cls.added_money > 0 && <span className="text-[#34C759]">+${cls.added_money}</span>}
                        {cls.arena && <span className="text-[#0096FF]">{cls.arena}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {/* Status Badge */}
                      {isAdmin && onSetClassStatus ? (
                        <select
                          value={status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { e.stopPropagation(); onSetClassStatus(cls.id, e.target.value); }}
                          className={`px-2 py-1 rounded text-xs font-bold border-0 cursor-pointer appearance-none ${cfg.bg} ${cfg.color}`}
                          style={{backgroundImage: 'none', paddingRight: '8px'}}
                          data-testid={`class-status-select-${cls.id}`}
                        >
                          <option value="UPCOMING">Upcoming</option>
                          <option value="LIVE">Live</option>
                          <option value="COMPLETED">Completed</option>
                        </select>
                      ) : (
                        status !== 'UPCOMING' && (
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 ${cfg.bg} ${cfg.color}`} data-testid={`class-status-badge-${cls.id}`}>
                            <StatusIcon size={10} weight="bold" /> {cfg.label}
                          </span>
                        )
                      )}
                      {(cls.rollover_from_class_ids?.length > 0 || cls.rollover_from_class_id) && (
                        <span className="px-2 py-0.5 bg-[#FF9500]/15 text-[#FF9500] rounded text-xs font-semibold flex items-center gap-1" data-testid={`rollover-badge-${cls.id}`} title="Other classes roll INTO this class">
                          <ArrowsClockwise size={10} weight="bold" /> RO IN{cls.rollover_category ? ` \u2022 ${cls.rollover_category}` : ''}{(cls.rollover_from_class_ids?.length || 0) > 1 ? ` (${cls.rollover_from_class_ids.length})` : ''}
                        </span>
                      )}
                      {(() => {
                        // "RO OUT" badge — count how many same-day classes accept
                        // rollovers from this one. Helps producer at a glance see
                        // a parent class's downstream rollover targets.
                        const allClasses = Object.values(classesByDay).flat();
                        const outCount = allClasses.filter((c) => {
                          if (c.id === cls.id) return false;
                          const ids = c.rollover_from_class_ids?.length
                            ? c.rollover_from_class_ids
                            : (c.rollover_from_class_id ? [c.rollover_from_class_id] : []);
                          return ids.includes(cls.id);
                        }).length;
                        if (outCount === 0) return null;
                        return (
                          <span className="px-2 py-0.5 bg-[#0096FF]/15 text-[#0096FF] rounded text-xs font-semibold flex items-center gap-1" data-testid={`rollover-out-badge-${cls.id}`} title="This class rolls OUT to other classes">
                            <ArrowsClockwise size={10} weight="bold" /> RO OUT ({outCount})
                          </span>
                        );
                      })()}
                      {cls.division_type && cls.division_type !== 'NONE' && (
                        <span className="px-2 py-0.5 bg-[#0096FF]/20 text-[#0096FF] rounded text-xs font-semibold">
                          {cls.division_type}
                        </span>
                      )}
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onConfigureDivisions(cls); }}
                          className="p-1.5 bg-[#262626] hover:bg-[#0096FF]/20 rounded transition-colors"
                          title="Configure Divisions"
                          data-testid={`config-divisions-${cls.id}`}
                        >
                          <Gear size={14} className="text-[#0096FF]" />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Watch Live button for riders/visitors */}
                  {status === 'LIVE' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/live/${cls.id}`); }}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-md text-[#FF3B30] text-sm font-bold hover:bg-[#FF3B30]/20 transition-colors"
                      data-testid={`watch-live-${cls.id}`}
                    >
                      <Eye size={16} weight="bold" /> Watch Live Results
                    </button>
                  )}
                  {/* View Results for completed classes */}
                  {status === 'COMPLETED' && !isAdmin && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/live/${cls.id}`); }}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#0096FF]/10 border border-[#0096FF]/30 rounded-md text-[#0096FF] text-sm font-bold hover:bg-[#0096FF]/20 transition-colors"
                      data-testid={`view-results-${cls.id}`}
                    >
                      <Eye size={16} weight="bold" /> View Results
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
